import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getAziendaUsage } from '../../../lib/services/statusService';
import { supabase } from '../../../lib/supabase';

const StatsCard = ({ onUpgradeClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profil } = await supabase
          .from('profili')
          .select('id_azienda')
          .eq('id', user.id)
          .single();

        if (profil?.id_azienda) {
          const res = await getAziendaUsage(profil.id_azienda);
          if (res.success) {
            setStats(res.data);
          }
        }
      }
      setLoading(false);
    };
    fetchStatus();
  }, []);

  if (loading) return <div className="h-64 w-full bg-muted/20 animate-pulse rounded-3xl mt-16" />;
  if (!stats) return null;

  const getBarColor = (proc) => {
    if (proc > 90) return 'bg-destructive';
    if (proc > 70) return 'bg-orange-500';
    return 'bg-primary';
  };

  return (
    <div className="w-full mt-4 animate-in fade-in slide-in-from-top-4 transition-all duration-700">
      
      {/* HEADER NAVIGATION - BACK & HISTORY */}
      <div className="flex justify-between items-center mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-all duration-300"
        >
          <div className="p-2 rounded-xl bg-muted/30 group-hover:bg-primary/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
          </div>
          <span className="text-[11px] font-black uppercase tracking-[0.2em]">
            {t('common1.back')}
          </span>
        </button>

        <button 
          onClick={() => navigate('/history_payments')} 
          className="group flex items-center gap-2.5 px-4 py-2 rounded-xl bg-card border border-border/60 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 active:scale-95"
        >
          <div className="text-primary group-hover:rotate-12 transition-transform">
            <Icon name="History" size={18} />
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-foreground/80 group-hover:text-primary transition-colors">
            {t('status.history_btn')}
          </span>
        </button>
      </div>

      {/* NASLOV SEKCIJE */}
      <div className="relative mb-10">
        <div className="flex flex-col relative z-10 bg-background pr-4 w-fit">
          <h2 className="text-2xl font-black uppercase tracking-tighter flex items-center gap-3 text-foreground/90">
            <div className="p-2 bg-primary rounded-lg text-primary-foreground shadow-lg shadow-primary/20">
              <Icon name="Activity" size={20} />
            </div>
            {t('status.section_title')}
          </h2>
          <p className="text-xs text-muted-foreground font-semibold mt-1 opacity-80 tracking-wide italic ml-12">
            {t('status.section_subtitle')}
          </p>
        </div>
        <div className="absolute bottom-[-20px] left-0 w-full h-[1px] bg-gradient-to-r from-border via-border to-transparent" />
      </div>

      <div className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* KARTICA 1: RADNICI */}
        <div className="bg-card p-7 rounded-[2.5rem] border border-border/60 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300">
          <div className="flex justify-between items-start mb-8">
            <div className="p-3 bg-primary/10 rounded-2xl text-primary">
              <Icon name="Users" size={24} />
            </div>
            <span className="text-[12px] font-black uppercase tracking-[0.3em] text-muted-foreground/50">
              {t('status.employees_label')}
            </span>
          </div>
          <div className="flex items-end justify-between mb-4">
            <h4 className="text-4xl font-black tracking-tighter">
              {stats.radnici.koristi} 
              <span className="text-lg text-muted-foreground/40 font-bold ml-2">/ {stats.radnici.limit}</span>
            </h4>
            <span className="text-xs font-black py-1 px-2.5 bg-muted rounded-full tracking-tighter">{stats.radnici.procenat}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-[700ms] ease-out ${getBarColor(stats.radnici.procenat)}`}
              style={{ width: `${stats.radnici.procenat}%` }}
            />
          </div>
        </div>

        {/* KARTICA 2: STORAGE */}
        <div className="bg-card p-7 rounded-[2.5rem] border border-border/60 shadow-sm hover:shadow-xl hover:border-blue-500/20 transition-all duration-300">
          <div className="flex justify-between items-start mb-8">
            <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-600">
              <Icon name="HardDrive" size={24} />
            </div>
            <span className="text-[12px] font-black uppercase tracking-[0.3em] text-muted-foreground/50">
              {t('status.storage_label')}
            </span>
          </div>
          <div className="flex items-end justify-between mb-4">
            <h4 className="text-4xl font-black tracking-tighter text-blue-600/90">
              {stats.storage.koristi} 
              <span className="text-lg text-muted-foreground/40 font-bold ml-2">/ {stats.storage.limit} <span className="text-[10px] uppercase tracking-normal">GB</span></span>
            </h4>
            <span className="text-xs font-black py-1 px-2.5 bg-muted rounded-full tracking-tighter">{stats.storage.procenat}%</span>
          </div>
          <div className="w-full h-3 bg-muted rounded-full overflow-hidden mb-6">
            <div 
              className={`h-full transition-all duration-[1500ms] ease-out ${getBarColor(stats.storage.procenat)}`}
              style={{ width: `${stats.storage.procenat}%` }}
            />
          </div>

          {/* DUGME PREBAČENO ISPOD BAR-A */}
          <button 
            onClick={() => navigate('/restore-documents')}
            className="w-full group flex items-center justify-center gap-2 py-3 rounded-2xl bg-blue-500/5 border border-blue-500/10 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 active:scale-95"
          >
            <Icon name="Trash2" size={16} className="group-hover:animate-bounce" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em]">
              {t('status.trash_btn', { defaultValue: 'Cestino' })}
            </span>
          </button>
        </div>

        {/* KARTICA 3: UGOVOR */}
        <div className={`p-7 rounded-[2.5rem] border transition-all duration-500 ${stats.ugovor.hitno ? 'bg-destructive/5 border-destructive/30 shadow-2xl shadow-destructive/10' : 'bg-card border-border/60 shadow-sm hover:shadow-xl'}`}>
          <div className="flex justify-between items-start mb-8">
            <div className={`p-3 rounded-2xl ${stats.ugovor.hitno ? 'bg-destructive text-white animate-bounce' : 'bg-emerald-500/10 text-emerald-600'}`}>
              <Icon name="Calendar" size={24} />
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[12px] font-black uppercase text-muted-foreground/40 tracking-widest mb-1">
                {t('status.current_plan')}
              </span>
              <span className="text-[11px] font-black uppercase tracking-tighter px-3 py-1 rounded-lg bg-foreground text-background">
                {stats.plan}
              </span>
            </div>
          </div>
          
          <div className="mb-8">
            <p className="text-[11px] font-bold text-muted-foreground/60 uppercase ml-1 mb-1 tracking-tight whitespace-nowrap">
              {t('status.expires_in')}
            </p>
            <div className="flex flex-col">
              <h4 className={`text-4xl font-black tracking-tighter ${stats.ugovor.hitno ? 'text-destructive' : 'text-foreground'} flex items-baseline`}>
                {stats.ugovor.preostalo} 
                <span className="text-lg opacity-40 font-bold uppercase italic ml-2">
                  {t('status.days_suffix')}
                </span>
              </h4>
              
              {stats.ugovor.kraj && (
                <div className="flex items-center gap-1.5 mt-1 ml-1 opacity-70">
                  <Icon name="Clock" size={12} className="text-muted-foreground" />
                  <span className="text-[10px] font-black tracking-widest text-muted-foreground uppercase">
                    {new Date(stats.ugovor.kraj).toLocaleDateString('de-DE')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <button 
            onClick={onUpgradeClick}
            className={`w-full py-4 rounded-2xl text-[11px] font-black tracking-[0.2em] transition-all transform active:scale-95 shadow-md ${
              stats.ugovor.hitno || stats.radnici.procenat > 85 
              ? 'bg-primary text-white shadow-primary/40 hover:bg-primary/90' 
              : 'bg-muted text-muted-foreground hover:bg-foreground hover:text-background'
            }`}
          >
            {t('status.upgrade_btn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatsCard;